import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, teamLeaderNavItems, workerNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  Building2,
  Send,
  CheckCircle2,
  AlertTriangle,
  Shield,
  HardHat,
  FileText,
  Info,
  UserPlus,
  Briefcase,
  Search,
  X,
  User,
  UserCircle,
  UsersRound,
  Star
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrowseProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Application dialog
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState("");
  
  // Team members
  const [workerCode, setWorkerCode] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchingWorker, setSearchingWorker] = useState(false);
  
  // My teams
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Fetch teams when dialog opens
  useEffect(() => {
    const fetchTeams = async () => {
      if (applyDialogOpen && user?.role === "team_leader") {
        try {
          const response = await axios.get(`${API}/teams`, {
            params: { my_teams: true }
          });
          setMyTeams(response.data || []);
        } catch (error) {
          console.error("Error fetching teams:", error);
        }
      }
    };
    fetchTeams();
  }, [applyDialogOpen, user]);

  // When team is selected, pre-populate members
  useEffect(() => {
    if (selectedTeam && selectedTeam !== "none") {
      const team = myTeams.find(t => t.team_id === selectedTeam);
      if (team && team.members) {
        setSelectedMembers(team.members.map(m => ({
          user_id: m.user_id,
          name: m.name,
          worker_code: m.worker_code,
          is_available: m.is_available
        })));
      }
    }
  }, [selectedTeam, myTeams]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/team/projects/${projectId}`);
      setProject(res.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error(error.response?.data?.detail || "Failed to load project");
      const redirectPath = user?.role === "worker" ? "/worker/browse-projects" : "/team-leader/browse-projects";
      navigate(redirectPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchWorker = async () => {
    if (!workerCode.trim()) return;
    
    setSearchingWorker(true);
    try {
      const response = await axios.get(`${API}/workers/search`, {
        params: { code: workerCode.trim().toUpperCase() }
      });
      
      if (response.data.length > 0) {
        const worker = response.data[0];
        
        // Check if worker is approved
        if (!worker.is_approved) {
          toast.error(`${worker.name} is not approved yet. Please wait for admin approval.`);
          return;
        }
        
        // Check if already added
        if (!selectedMembers.find(m => m.user_id === worker.user_id)) {
          setSelectedMembers([...selectedMembers, worker]);
          setWorkerCode("");
          toast.success(`${worker.name} added to team`);
        } else {
          toast.info("Worker already in team");
        }
      } else {
        toast.error("Worker not found or not approved yet");
      }
    } catch (error) {
      toast.error("Failed to search worker");
    } finally {
      setSearchingWorker(false);
    }
  };

  const handleRemoveMember = (userId) => {
    setSelectedMembers(selectedMembers.filter(m => m.user_id !== userId));
  };

  const handleApply = async () => {
    if (user?.role !== "team_leader") {
      toast.error("Only Team Leaders can apply for projects");
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/team/projects/${projectId}/apply`, {
        message: applicationMessage,
        team_members: selectedMembers.map(m => m.user_id)
      });
      toast.success("Application submitted successfully!");
      setApplyDialogOpen(false);
      fetchProject();  // Refresh to show applied status
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setApplying(false);
    }
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      simple: "bg-green-500/20 text-green-400 border-green-500/30",
      medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      complex: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return colors[complexity] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    const backPath = user?.role === "worker" ? "/worker/browse-projects" : "/team-leader/browse-projects";
    return (
      <DashboardLayout navItems={navItems} title="Project Details">
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-[#475569]" />
          <p className="text-[#94A3B8]">Project not found or no longer accepting applications</p>
          <Button 
            className="mt-4 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
            onClick={() => navigate(backPath)}
          >
            Browse Projects
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const backPath = user?.role === "worker" ? "/worker/browse-projects" : "/team-leader/browse-projects";

  return (
    <DashboardLayout navItems={navItems} title="Project Details">
      <div className="space-y-6" data-testid="browse-project-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10" 
              onClick={() => navigate(backPath)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{project.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-emerald-500/20 text-emerald-400">Live</Badge>
                <Badge className={`capitalize ${project.project_type === "assembly" ? "bg-[#23F7DD]/20 text-[#23F7DD]" : "bg-orange-500/20 text-orange-400"}`}>
                  {project.project_type}
                </Badge>
                <Badge className={getComplexityColor(project.complexity)}>
                  {project.complexity}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Details */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#23F7DD]" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.description && (
                  <p className="text-[#94A3B8]">{project.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                      <MapPin className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-xs uppercase">Location</span>
                    </div>
                    <p className="text-white">{project.location_city}</p>
                    {project.location_address && (
                      <p className="text-sm text-[#94A3B8]">{project.location_address}</p>
                    )}
                  </div>
                  
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                      <Calendar className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-xs uppercase">Start Date</span>
                    </div>
                    <p className="text-white">
                      {project.start_date ? new Date(project.start_date).toLocaleDateString() : "Flexible"}
                    </p>
                  </div>
                  
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                      <Users className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-xs uppercase">Crew Size</span>
                    </div>
                    <p className="text-white">{project.crew_size || "N/A"} workers</p>
                  </div>
                  
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                      <Clock className="w-4 h-4 text-[#23F7DD]" />
                      <span className="text-xs uppercase">Duration</span>
                    </div>
                    <p className="text-white">{project.estimated_duration || "N/A"} hours</p>
                  </div>
                </div>

                <div className="p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 text-[#94A3B8] mb-1">
                    <Building2 className="w-4 h-4 text-[#23F7DD]" />
                    <span className="text-xs uppercase">Client</span>
                  </div>
                  <p className="text-white">{project.client_name || "Private Client"}</p>
                </div>
              </CardContent>
            </Card>

            {/* SJA (Safety Job Analysis) */}
            {project.sja_form && (project.sja_form.hazards?.filter(h => h && (h.hazard || h.description)).length > 0 || project.sja_form.ppe_required?.length > 0) && (
              <Card className="bg-[#0A0A0A] border-red-500/30">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Safety Job Analysis (SJA)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.sja_form.hazards?.filter(h => h && (h.hazard || h.description)).length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Identified Hazards:</p>
                      <div className="space-y-2">
                        {project.sja_form.hazards.filter(h => h && (h.hazard || h.description)).map((hazard, idx) => (
                          <div key={idx} className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                                hazard.risk_level === "high" ? "text-red-400" : 
                                hazard.risk_level === "medium" ? "text-yellow-400" : "text-green-400"
                              }`} />
                              <div>
                                <p className="text-white font-medium">{hazard.hazard || hazard.description}</p>
                                <p className="text-sm text-[#94A3B8]">
                                  Risk: <span className="capitalize">{hazard.risk_level}</span>
                                </p>
                                {hazard.mitigation && (
                                  <p className="text-sm text-[#94A3B8] mt-1">
                                    Mitigation: {hazard.mitigation}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {project.sja_form.ppe_required?.length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Required PPE:</p>
                      <div className="flex flex-wrap gap-2">
                        {project.sja_form.ppe_required.map((ppe, idx) => (
                          <Badge key={idx} className="bg-red-500/20 text-red-300 border border-red-500/30">
                            <HardHat className="w-3 h-3 mr-1" />
                            {ppe}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Project Images & Documents */}
            {(project.project_images?.length > 0 || project.project_documents?.length > 0) && (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#23F7DD]" />
                    Filer og dokumenter
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Project Images */}
                  {project.project_images?.length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Prosjektbilder:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {project.project_images.map((img, idx) => (
                          <a 
                            key={idx}
                            href={img.url || img}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded overflow-hidden hover:opacity-80 transition-opacity"
                          >
                            <img 
                              src={img.url || img} 
                              alt={img.name || `Bilde ${idx + 1}`}
                              className="w-full h-24 object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Project Documents */}
                  {project.project_documents?.length > 0 && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Dokumenter:</p>
                      <div className="space-y-2">
                        {project.project_documents.map((doc, idx) => (
                          <a 
                            key={idx}
                            href={doc.url || doc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 bg-white/5 rounded hover:bg-white/10 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-blue-400" />
                            <span className="text-blue-400 text-sm hover:underline">
                              {doc.name || doc.filename || `Dokument ${idx + 1}`}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Right column */}
          <div className="space-y-6">
            {/* Budget Card */}
            <Card className="bg-gradient-to-br from-[#23F7DD]/10 to-[#4F46E5]/10 border-[#23F7DD]/30">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-sm text-[#94A3B8] uppercase tracking-wider mb-2">Brutto Lønn (før skatt)</p>
                  <p className="text-3xl font-['Unbounded'] font-bold text-[#23F7DD]">
                    {(() => {
                      // First check if net_to_team is directly available from API
                      if (project.net_to_team) {
                        return `${Math.round(project.net_to_team).toLocaleString()} NOK`;
                      }
                      
                      // Check fee_breakdown if available
                      if (project.fee_breakdown?.net_to_team) {
                        return `${Math.round(project.fee_breakdown.net_to_team).toLocaleString()} NOK`;
                      }
                      
                      // Try budget_min/budget_max first
                      const min = project.budget_min;
                      const max = project.budget_max;
                      
                      if (min && max && min === max) {
                        return `${min.toLocaleString()} NOK`;
                      } else if (min && max) {
                        return `${min.toLocaleString()} - ${max.toLocaleString()} NOK`;
                      } else if (min) {
                        return `From ${min.toLocaleString()} NOK`;
                      } else if (max) {
                        return `Up to ${max.toLocaleString()} NOK`;
                      }
                      
                      // Calculate from team_budget or approved_budget
                      const baseBudget = project.team_budget || project.approved_budget;
                      if (baseBudget) {
                        // Apply Norwegian deductions (AGA 14.5%, feriepenger 10.8%, other 3%)
                        const totalDeductionRate = 28.3; // 14.5 + 10.8 + 3
                        const netForWorkers = baseBudget * (1 - totalDeductionRate / 100);
                        return `${Math.round(netForWorkers).toLocaleString()} NOK`;
                      }
                      
                      return "Price on request";
                    })()}
                  </p>
                  <p className="text-xs text-[#94A3B8] mt-2">
                    Brutto lønn til teamet - skatt trekkes individuelt
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Application Card */}
            <Card className={`bg-[#0A0A0A] ${project.already_applied ? 'border-green-500/30' : 'border-[#23F7DD]/30'}`}>
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  {project.already_applied ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      Application Submitted
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 text-[#23F7DD]" />
                      Apply for Project
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.already_applied ? (
                  <div>
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg mb-4">
                      <p className="text-green-400 text-sm">
                        Your application has been submitted. The client will review it and contact you if selected.
                      </p>
                    </div>
                    {project.existing_application && (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Status</span>
                          <Badge className={
                            project.existing_application.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                            project.existing_application.status === "accepted" ? "bg-green-500/20 text-green-400" :
                            "bg-red-500/20 text-red-400"
                          }>
                            {project.existing_application.status}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#94A3B8]">Applied</span>
                          <span className="text-white">
                            {new Date(project.existing_application.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {project.existing_application.team_members?.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#94A3B8]">Team Size</span>
                            <span className="text-white">
                              {project.existing_application.team_members.length + 1} members
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : user?.role === "team_leader" ? (
                  <div className="space-y-4">
                    <Button 
                      onClick={() => setApplyDialogOpen(true)}
                      className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold py-6"
                      data-testid="open-apply-dialog-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Apply for This Project
                    </Button>
                    
                    <p className="text-xs text-center text-[#94A3B8]">
                      You can add team members to your application
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-400">
                      <Info className="w-5 h-5" />
                      <span>Only Team Leaders can apply for projects</span>
                    </div>
                    <p className="text-sm text-[#94A3B8] mt-2">
                      Contact your Team Leader to apply for this project.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#94A3B8] mt-0.5" />
                  <div className="text-sm text-[#94A3B8]">
                    <p className="mb-2">After applying:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Client reviews applications</li>
                      <li>If selected, you'll receive a contract</li>
                      <li>Sign the contract to confirm</li>
                      <li>Project becomes active</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Apply Dialog */}
        <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] text-xl">Apply for Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Project Info */}
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white font-medium">{project.title}</p>
                <p className="text-sm text-[#94A3B8]">{project.location_city}</p>
              </div>

              {/* Team Leader (You) */}
              <div className="space-y-3">
                <Label className="text-white flex items-center gap-2">
                  <UserCircle className="w-4 h-4 text-[#23F7DD]" />
                  Team Leader
                </Label>
                <div className="p-3 bg-[#23F7DD]/10 rounded-lg border border-[#23F7DD]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-[#23F7DD]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{user?.name || user?.company_name} (You)</p>
                        <p className="text-xs text-[#23F7DD]">{user?.leader_code || user?.worker_code || "Team Leader"}</p>
                      </div>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-[#23F7DD]" />
                  </div>
                </div>
              </div>

              {/* Team Selection */}
              {myTeams.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    <UsersRound className="w-4 h-4 text-[#23F7DD]" />
                    Select Your Team (Optional)
                  </Label>
                  <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]">
                      <SelectValue placeholder="Choose a saved team" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="none" className="text-[#94A3B8] hover:bg-white/10">
                        No team (add members manually)
                      </SelectItem>
                      {myTeams.map((team) => (
                        <SelectItem 
                          key={team.team_id} 
                          value={team.team_id}
                          className="text-white hover:bg-white/10"
                        >
                          {team.name} ({team.members?.length || 0} members)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Add Team Members by Code */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  <Search className="w-4 h-4 text-[#23F7DD]" />
                  Add Team Member by Worker Code
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={workerCode}
                    onChange={(e) => setWorkerCode(e.target.value.toUpperCase())}
                    placeholder="e.g., WRK-001"
                    className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                    onKeyDown={(e) => e.key === "Enter" && handleSearchWorker()}
                  />
                  <Button 
                    onClick={handleSearchWorker} 
                    disabled={searchingWorker}
                    className="bg-[#23F7DD]/20 text-[#23F7DD] hover:bg-[#23F7DD]/30 border border-[#23F7DD]/30"
                  >
                    {searchingWorker ? "..." : "Add"}
                  </Button>
                </div>
                <p className="text-xs text-[#94A3B8]">Enter the worker's personal ID code (WRK-XXX) to add them</p>
              </div>

              {/* Selected Team Members */}
              {selectedMembers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-white">Team Members ({selectedMembers.length})</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedMembers.map((member) => (
                      <div 
                        key={member.user_id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${member.is_available !== false ? 'bg-green-400' : 'bg-red-400'}`} />
                          <div>
                            <p className="text-white font-medium">{member.name}</p>
                            <p className="text-xs text-[#94A3B8]">
                              {member.worker_code} • {member.is_available !== false ? 'Available' : 'Unavailable'}
                              {member.average_rating > 0 && (
                                <span className="ml-2 inline-flex items-center">
                                  <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                  {member.average_rating.toFixed(1)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Application Message */}
              <div>
                <Label className="text-[#94A3B8]">Message (optional)</Label>
                <Textarea
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  placeholder="Introduce yourself, mention your experience..."
                  className="mt-2 bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                  rows={4}
                />
              </div>

              {/* Apply Button */}
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setApplyDialogOpen(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleApply}
                  disabled={applying}
                  className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                  data-testid="submit-application-btn"
                >
                  {applying ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
